import React from 'react';
import { Button, Modal } from '@mui/material';
import './DurationPickerModal.scss';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

interface DurationValue {
  hours: number;
  minutes: number;
}

interface DurationPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (hours: number, minutes: number) => void;
  title: string;
  initialDurationMin: number;
}

export const DurationPickerModal: React.FC<DurationPickerModalProps> = ({
  isOpen,
  onClose,
  onSave,
  title,
  initialDurationMin,
}) => {
  const { t } = useTranslation();
  const [durationValue, setDurationValue] = React.useState<DurationValue>(() => ({
    hours: Math.floor(initialDurationMin / 60),
    minutes: initialDurationMin % 60,
  }));

  React.useEffect(() => {
    if (isOpen) {
      setDurationValue({
        hours: Math.floor(initialDurationMin / 60),
        minutes: initialDurationMin % 60,
      });
    }
  }, [isOpen, initialDurationMin]);

  const handleHoursChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const hours = parseInt(e.target.value) || 0;
    setDurationValue((prev) => ({
      ...prev,
      hours: Math.min(Math.max(hours, 0), 24),
    }));
  };

  const handleMinutesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const minutes = parseInt(e.target.value) || 0;
    setDurationValue((prev) => ({
      ...prev,
      minutes: Math.min(Math.max(minutes, 0), 59),
    }));
  };

  const handleSave = () => {
    onSave(durationValue.hours, durationValue.minutes);
    onClose();
  };

  const isDisabled = durationValue.hours === 0 && durationValue.minutes === 0;

  return (
    <Modal open={isOpen} onClose={onClose} className="duration-picker-modal">
      <div className="duration-picker-modal__content">
        <h3>⏳ {title}</h3>
        <div className="duration-picker-modal__duration-picker">
          <input
            type="number"
            min={0}
            max={24}
            value={durationValue.hours}
            onChange={handleHoursChange}
            className="duration-picker-modal__duration-input"
          />
          <span>{t('settings.common.hoursShort')}</span>
          <input
            type="number"
            min={0}
            max={59}
            value={durationValue.minutes}
            onChange={handleMinutesChange}
            className="duration-picker-modal__duration-input"
          />
          <span>{t('settings.common.minutesShort')}</span>
        </div>
        <div className="duration-picker-modal__actions">
          <Button variant="text" onClick={onClose} className="duration-picker-modal__cancel-button">
            {t('settings.common.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            className={clsx('duration-picker-modal__save-button', {
              'duration-picker-modal__save-button--disabled': isDisabled,
            })}
            disabled={isDisabled}
          >
            {t('settings.common.save')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
